/**
 * PaymentDetails Component
 * 
 * Detailed view of a single payment with all metadata and actions
 */
import React, { useState } from 'react';
import { 
  ArrowLeft,
  Copy,
  ExternalLink,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  DollarSign,
  User,
  Calendar,
  CreditCard,
  AlertTriangle,
  FileText
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../common';
import { H3, Text } from '../../../common/Typography';
import { Button } from '../../../common';
import RefundDialog from './RefundDialog';

const PaymentDetails = ({ payment, onBack }) => {
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [copiedField, setCopiedField] = useState('');

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount / 100);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(''), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'succeeded':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'refunded':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'partially_refunded':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'succeeded':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'refunded':
        return <RefreshCw className="h-5 w-5 text-blue-500" />;
      case 'partially_refunded':
        return <AlertTriangle className="h-5 w-5 text-orange-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const canRefund = payment.status === 'succeeded' || payment.status === 'partially_refunded';
  const totalRefunded = payment.refunds?.reduce((sum, refund) => sum + (refund.amount || 0), 0) || 0;
  const refundableAmount = payment.amount - totalRefunded;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            onClick={onBack}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to List
          </Button>
          <div>
            <H3>Payment Details</H3>
            <Text className="text-gray-600">#{payment.id}</Text>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {getStatusIcon(payment.status)}
          <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(payment.status)}`}>
            {payment.status.replace('_', ' ').toUpperCase()}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Payment Info */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Payment Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Text className="text-sm font-medium text-gray-500 mb-1">Payment ID</Text>
                  <div className="flex items-center gap-2">
                    <Text className="font-mono text-sm">{payment.id}</Text>
                    <button
                      onClick={() => copyToClipboard(payment.id, 'id')}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    {copiedField === 'id' && (
                      <span className="text-green-600 text-xs">Copied!</span>
                    )}
                  </div>
                </div>
                <div>
                  <Text className="text-sm font-medium text-gray-500 mb-1">Stripe Payment Intent</Text>
                  <div className="flex items-center gap-2">
                    <Text className="font-mono text-sm">{payment.stripePaymentIntentId}</Text>
                    <button
                      onClick={() => copyToClipboard(payment.stripePaymentIntentId, 'stripe')}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    {copiedField === 'stripe' && (
                      <span className="text-green-600 text-xs">Copied!</span>
                    )}
                    <a
                      href={`https://dashboard.stripe.com/payments/${payment.stripePaymentIntentId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#00796b] hover:text-[#004d40]"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                </div>
                <div>
                  <Text className="text-sm font-medium text-gray-500 mb-1">Amount</Text>
                  <Text className="text-lg font-semibold">{formatCurrency(payment.amount)}</Text>
                </div>
                <div>
                  <Text className="text-sm font-medium text-gray-500 mb-1">Currency</Text>
                  <Text className="uppercase">{payment.currency}</Text>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Customer Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Text className="text-sm font-medium text-gray-500 mb-1">Email</Text>
                  <Text>{payment.customerEmail}</Text>
                </div>
                <div>
                  <Text className="text-sm font-medium text-gray-500 mb-1">User ID</Text>
                  <div className="flex items-center gap-2">
                    <Text className="font-mono text-sm">{payment.userId}</Text>
                    <button
                      onClick={() => copyToClipboard(payment.userId, 'userId')}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    {copiedField === 'userId' && (
                      <span className="text-green-600 text-xs">Copied!</span>
                    )}
                  </div>
                </div>
                <div>
                  <Text className="text-sm font-medium text-gray-500 mb-1">Application ID</Text>
                  <div className="flex items-center gap-2">
                    <Text className="font-mono text-sm">{payment.applicationId}</Text>
                    <button
                      onClick={() => copyToClipboard(payment.applicationId, 'appId')}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    {copiedField === 'appId' && (
                      <span className="text-green-600 text-xs">Copied!</span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Timeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <div className="flex-1">
                    <Text className="font-medium">Payment Created</Text>
                    <Text className="text-sm text-gray-500">{formatDate(payment.createdAt)}</Text>
                  </div>
                </div>
                {payment.paidAt && (
                  <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <div className="flex-1">
                      <Text className="font-medium">Payment Succeeded</Text>
                      <Text className="text-sm text-gray-500">{formatDate(payment.paidAt)}</Text>
                    </div>
                  </div>
                )}
                {payment.refundedAt && (
                  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <div className="flex-1">
                      <Text className="font-medium">Payment Refunded</Text>
                      <Text className="text-sm text-gray-500">{formatDate(payment.refundedAt)}</Text>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {canRefund && refundableAmount > 0 && (
                <Button
                  onClick={() => setShowRefundDialog(true)}
                  className="w-full flex items-center gap-2"
                  variant="outline"
                >
                  <RefreshCw className="h-4 w-4" />
                  Process Refund
                </Button>
              )}
              <Button
                onClick={() => window.open(`https://dashboard.stripe.com/payments/${payment.stripePaymentIntentId}`, '_blank')}
                className="w-full flex items-center gap-2"
                variant="outline"
              >
                <ExternalLink className="h-4 w-4" />
                View in Stripe
              </Button>
            </CardContent>
          </Card>

          {/* Refund Information */}
          {payment.refunds && payment.refunds.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Refunds</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <Text className="text-gray-500">Total Refunded:</Text>
                    <Text className="font-medium text-red-600">
                      -{formatCurrency(totalRefunded)}
                    </Text>
                  </div>
                  <div className="flex justify-between text-sm">
                    <Text className="text-gray-500">Refundable:</Text>
                    <Text className="font-medium">
                      {formatCurrency(refundableAmount)}
                    </Text>
                  </div>
                  <div className="border-t pt-3 space-y-2">
                    {payment.refunds.map((refund, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <Text className="text-gray-500">
                          {formatDate(refund.createdAt)}
                        </Text>
                        <Text className="font-medium text-red-600">
                          -{formatCurrency(refund.amount)}
                        </Text>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Metadata */}
          {payment.metadata && (
            <Card>
              <CardHeader>
                <CardTitle>Metadata</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="text-xs bg-gray-50 p-3 rounded-lg overflow-x-auto">
                  {JSON.stringify(payment.metadata, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Refund Dialog */}
      {showRefundDialog && (
        <RefundDialog
          payment={payment}
          maxRefundAmount={refundableAmount}
          onClose={() => setShowRefundDialog(false)}
          onSuccess={(updatedPayment) => {
            setShowRefundDialog(false);
            // Payment would be updated by parent component
          }}
        />
      )}
    </div>
  );
};

export default PaymentDetails;
